"use client";

import clsx from "clsx";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import GailMessage from "../GailMessage";

interface ContactFormProps {
  onReset?: () => void;
  assistantType?: "insurance" | "banking" | "finance";
}

const ContactForm = ({
  onReset,
  assistantType = "insurance",
}: ContactFormProps = {}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<
    { text: string; isUser: boolean; role?: string; isAnimated?: boolean }[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactInfo, setContactInfo] = useState<{
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phoneNumber: string | null;
  } | null>(null);
  const [isInfoComplete, setIsInfoComplete] = useState(false);
  const [callStatus, setCallStatus] = useState<"calling" | null>(null);
  const [showInterface, setShowInterface] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom of messages within the chat container only
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    // Use a small timeout to ensure the DOM has updated
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [messages, isSubmitting, callStatus]);

  // Component mount effect - initialize the conversation
  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setMessages([]);
    setContactInfo(null);
    setIsInfoComplete(false);
    setCallStatus(null);

    // Add initial Gail message with animation
    setMessages([
      {
        text: t(
          "contact.gail_intro",
          "Hi there! I am Gail. Who do I have the pleasure of speaking with today?"
        ),
        isUser: false,
        role: "assistant",
        isAnimated: true,
      },
    ]);

    // Show interface immediately
    setShowInterface(true);

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, [t]);

  // Modified useEffect to handle confirmation and call flow properly
  useEffect(() => {
    // Only start the call if info is complete AND we're not in a call already AND no API error
    if (isInfoComplete && contactInfo && !callStatus && !hasApiError) {
      // Set calling state
      setCallStatus("calling");

      // Call the API to register the contact information
      fetch("/api/call-me", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: contactInfo.firstName,
          lastName: contactInfo.lastName,
          email: contactInfo.email,
          phoneNumber: contactInfo.phoneNumber,
          assistantType: assistantType,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to register contact");
          }
          return response.json();
        })
        .catch((error) => {
          console.error("Error registering contact:", error);
          // Show error message to user
          setMessages((prev) => [
            ...prev,
            {
              text: "I'm sorry, there was an issue connecting the call. Please try again later.",
              isUser: false,
              role: "assistant",
              isAnimated: true,
            },
          ]);
          // Reset call status and set the error flag to prevent retries
          setCallStatus(null);
          setHasApiError(true);
        });
    }
  }, [isInfoComplete, contactInfo, callStatus, hasApiError, assistantType]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim() || isSubmitting) return;

    // Add user message
    const userMessage = inputValue;
    setMessages((prev) => [
      ...prev,
      { text: userMessage, isUser: true, role: "user" },
    ]);
    setInputValue("");

    // Reset textarea height and scrolling
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.overflowY = "hidden";
    }

    // Add typing indicator
    setIsSubmitting(true);

    // Focus the input again immediately after user submits
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);

    // Get OpenAI to process the conversation so far
    try {
      // Prepare conversation history for API
      const conversationHistory = messages.map((msg) => ({
        role: msg.isUser ? "user" : "assistant",
        content: msg.text,
      }));

      // Add the current user message
      conversationHistory.push({ role: "user", content: userMessage });

      // Include previous collected info in developer notes to help with context persistence
      const systemNote = contactInfo
        ? `Previous collected info: ${JSON.stringify(contactInfo)}`
        : "";

      if (systemNote) {
        // Add a system message with the current collected info to help the model keep track
        conversationHistory.push({
          role: "system",
          content: systemNote,
        });
      }

      // Call our API endpoint
      const response = await fetch("/api/contact-info", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ messages: conversationHistory }),
      });

      if (!response.ok) {
        throw new Error("Failed to process message");
      }

      const data = await response.json();

      // Update contact info state with new info from AI
      setContactInfo(data.contactInfo);

      // Set info complete flag
      setIsInfoComplete(data.isComplete);

      // Add AI response
      setMessages((prev) => [
        ...prev,
        {
          text: data.message,
          isUser: false,
          role: "assistant",
          isAnimated: true,
        },
      ]);
    } catch (error) {
      console.error("Error processing message:", error);
      setMessages((prev) => [
        ...prev,
        {
          text: "Sorry, I am having trouble processing your information. Please try again.",
          isUser: false,
          role: "assistant",
          isAnimated: true,
        },
      ]);
    } finally {
      setIsSubmitting(false);
      // Focus the input after message is sent with a small delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
    }
  };

  // Add effect to maintain focus on input
  useEffect(() => {
    if (inputRef.current && showInterface) {
      inputRef.current.focus();
    }
  }, [messages, isSubmitting, showInterface]); // Re-focus whenever these change

  // Initialize textarea when interface is shown
  useEffect(() => {
    if (showInterface && inputRef.current) {
      // Initialize textarea height and scrolling
      inputRef.current.style.height = "auto";
      inputRef.current.style.overflowY = "hidden";
    }
  }, [showInterface]);

  // Reset button handler
  const handleResetConversation = () => {
    // Reset all states
    setMessages([]);
    setContactInfo(null);
    setIsInfoComplete(false);
    setCallStatus(null);
    setShowInterface(false);
    setHasApiError(false);

    // Call the onReset prop if provided to reset parent state
    if (onReset) {
      onReset();
      return; // Exit early as the component will unmount
    }

    // Add initial message again
    setMessages([
      {
        text: t(
          "contact.gail_intro",
          "Hi there! I am Gail. Who do I have the pleasure of speaking with today?"
        ),
        isUser: false,
        role: "assistant",
        isAnimated: true,
      },
    ]);

    // Show the interface after a delay
    setTimeout(() => setShowInterface(true), 1000);
  };

  return (
    <div className="flex flex-col h-full font-sans">
      <div
        ref={chatContainerRef}
        className="flex-grow overflow-y-auto relative"
        style={{ maxHeight: "calc(100% - 70px)" }}
      >
        {messages.map((message, index) => (
          <div
            key={index}
            className={clsx(
              "mb-4 px-3",
              message.isUser ? "flex justify-end" : ""
            )}
          >
            {message.isUser ? (
              <div className="bg-gail-primary text-white p-2 rounded-lg max-w-[75%]">
                <p className="text-sm">{message.text}</p>
              </div>
            ) : (
              <GailMessage
                message={message.text}
                startAnimation={message.isAnimated}
              />
            )}
          </div>
        ))}
        {isSubmitting && (
          <div className="mb-4 px-3">
            <div className="max-w-[80%]">
              <div className="mb-2">
                <Image
                  src="/images/gail_icon_black.svg"
                  alt="Gail Icon"
                  width={16}
                  height={16}
                  className="inline-block mr-2"
                />
                <span className="font-bold align-middle text-sm">• Gail</span>
              </div>
              <div className="text-sm leading-relaxed">
                <span className="inline-block animate-pulse">▋</span>
              </div>
            </div>
          </div>
        )}

        {/* Show progress indicators */}
        {contactInfo && !isInfoComplete && !callStatus && (
          <div className="mt-4 mb-2">
            <p className="text-xs text-gray-500 mb-2">
              Information collected so far:
            </p>
            <div className="flex flex-wrap gap-2">
              <span
                className={clsx(
                  "text-xs px-2 py-1 rounded-full",
                  contactInfo.firstName
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-400"
                )}
              >
                First Name {contactInfo.firstName ? "✓" : ""} (Required)
              </span>
              <span
                className={clsx(
                  "text-xs px-2 py-1 rounded-full",
                  contactInfo.lastName
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-400"
                )}
              >
                Last Name {contactInfo.lastName ? "✓" : ""} (Optional)
              </span>
              <span
                className={clsx(
                  "text-xs px-2 py-1 rounded-full",
                  contactInfo.email
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-400"
                )}
              >
                Email {contactInfo.email ? "✓" : ""} (Optional)
              </span>
              <span
                className={clsx(
                  "text-xs px-2 py-1 rounded-full",
                  contactInfo.phoneNumber
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-400"
                )}
              >
                Phone {contactInfo.phoneNumber ? "✓" : ""} (Required)
              </span>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSendMessage}
        className={clsx(
          "mt-auto transition-opacity duration-300 mx-2",
          showInterface ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="relative">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);

              // Auto-grow textarea height based on content
              if (inputRef.current) {
                // Reset height to calculate scroll height correctly
                inputRef.current.style.height = "auto";

                // Calculate new height based on content
                const scrollHeight = inputRef.current.scrollHeight;
                const lineHeight = 24; // Assuming 24px line height
                const maxHeight = lineHeight * 3; // 3 lines maximum

                if (scrollHeight <= maxHeight) {
                  // If content fits within 3 lines, grow automatically
                  inputRef.current.style.height = `${scrollHeight}px`;
                  inputRef.current.style.overflowY = "hidden";
                } else {
                  // If content exceeds 3 lines, set to max height and enable scrolling
                  inputRef.current.style.height = `${maxHeight}px`;
                  inputRef.current.style.overflowY = "auto";
                }
              }
            }}
            placeholder={t(
              "contact.message_placeholder",
              "Type your message..."
            )}
            disabled={isSubmitting}
            autoFocus={true}
            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gail-primary pr-12 resize-none"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && isApproved) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
          />
          <button
            type="submit"
            disabled={isSubmitting || !isApproved}
            className={clsx(
              "absolute",
              "right-2",
              "top-1",
              "bg-gail-primary",
              "text-white",
              "p-2",
              "rounded-full",
              "hover:bg-gail-primary/80",
              "focus:outline-none",
              "disabled:!bg-gail-primary/50"
            )}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          <input
            type="checkbox"
            id="approvalCheckbox"
            checked={isApproved}
            onChange={(e) => setIsApproved(e.target.checked)}
            className="mr-2 float-left"
          />
          <label htmlFor="approvalCheckbox">
            <Trans i18nKey="contactPrivacy">
              <div>
                By submitting this form, you agree to receive recurring
                automated product updates and operational update texts from Gail
                to the number entered. Consent isn&apos;t a condition of any
                service. Reply HELP for help or STOP to cancel.
              </div>
              <div className="mt-1">
                Message frequency may vary. Msg & data rates may apply. View our{" "}
              </div>
              <a
                href="/terms-condition"
                className={clsx("underline", "text-primary")}
              >
                Terms
              </a>
              &amp;
              <a
                href="/privacy-policy"
                className={clsx("underline", "text-primary")}
              >
                Privacy
              </a>
              .
            </Trans>
          </label>
        </p>
        {isInfoComplete && (
          <div className="text-center mt-3">
            <button
              onClick={handleResetConversation}
              className="text-xs px-4 py-2 bg-gail-primary text-white rounded-full hover:bg-gail-primary/80 transition-colors"
            >
              {t("hero.startNewConversation")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
